import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  Stack,
  TextField,
  Paper,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AiCardBase from './AiCardBase';

const RisksAiCard = ({ card, onConfirm, isConfirmed, isConfirming }) => {
  const { cardType, label, data = {} } = card;

  const rawRisks = Array.isArray(data)
    ? data
    : Array.isArray(data.risks)
    ? data.risks
    : data.riskName
    ? [data]
    : [];

  const [notes, setNotes] = useState(data.notes || '');

  useEffect(() => {
    setNotes(data.notes || '');
  }, [data]);

  const handleConfirm = () => {
    onConfirm(cardType, { ...data, notes });
  };

  const getSeverityColor = (severity = '') => {
    const s = severity.toUpperCase();
    if (s.includes('HIGH')) return { color: '#DC2626', bg: '#FEF2F2' };
    if (s.includes('MODERATE') || s.includes('MEDIUM')) return { color: '#D97706', bg: '#FFFBEB' };
    return { color: '#2563EB', bg: '#EFF6FF' };
  };

  return (
    <AiCardBase
      cardType={cardType}
      label={label || 'Patient Risk Factors'}
      isConfirmed={isConfirmed}
      isConfirming={isConfirming}
      onConfirm={handleConfirm}
      isUpdatedByAi={card.isUpdatedByAi}
    >
      <Box sx={{ py: 1 }}>
        {rawRisks.length > 0 ? (
          <Stack spacing={1.5} mb={2}>
            {rawRisks.map((risk, idx) => {
              const name = risk.riskName || risk.name || risk.factor || 'Risk Factor';
              const sev = risk.severity || risk.level || 'Moderate';
              const colors = getSeverityColor(sev);

              return (
                <Paper
                  key={idx}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderColor: '#E2E8F0',
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <WarningAmberIcon sx={{ color: colors.color, fontSize: 20 }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {name}
                      </Typography>
                      {risk.description && (
                        <Typography variant="caption" sx={{ color: '#64748B' }}>
                          {risk.description}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                  <Chip
                    label={sev}
                    size="small"
                    sx={{
                      bgcolor: colors.bg,
                      color: colors.color,
                      fontWeight: 700,
                      fontSize: 11,
                    }}
                  />
                </Paper>
              );
            })}
          </Stack>
        ) : (
          <Typography variant="body2" sx={{ color: '#64748B', mb: 2 }}>
            Risk assessment extracted from discussion.
          </Typography>
        )}

        <TextField
          fullWidth
          size="small"
          label="Risk Assessment Notes"
          multiline
          rows={2}
          disabled={isConfirmed}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Additional notes on patient risk assessment..."
          data-testid="input-risk-notes"
        />
      </Box>
    </AiCardBase>
  );
};

export default RisksAiCard;
